/**
 * SoftPhone SIP.js wrapper: REGISTER / INVITE / accept / hangup / DTMF.
 * WebSocket: wss://currentHost + credentials.websocket_url (path relativo).
 * Logs de registro en memoria para la pestaña Logs de SoftphonePage.
 */
import {
  Invitation,
  Inviter,
  Registerer,
  RegistererState,
  Session,
  SessionState,
  UserAgent,
  UserAgentState,
} from 'sip.js';
import type { SoftphoneCredentials } from './softphoneApi';
import { eventBus } from '../EventBus';
import {
  evaluateLocalDnd,
  headerIndicator,
  type SoftphoneDndEffective,
  type SoftphoneDndRule,
  type SoftphoneDndScope,
} from '../../utils/softphoneDnd';
import { isDoorbellIncomingContext } from '../../utils/sessionFlags';

type PeerConnectionLike = {
  getReceivers: () => Array<{ track: MediaStreamTrack | null }>;
  getSenders: () => Array<{
    track: MediaStreamTrack | null;
    replaceTrack?: (track: MediaStreamTrack | null) => Promise<RTCRtpSender | void>;
  }>;
};

function getPeerConnection(session: Session): PeerConnectionLike | null {
  const sdh = session.sessionDescriptionHandler as { peerConnection?: PeerConnectionLike } | undefined;
  return sdh?.peerConnection || null;
}

function isAndroidUa(): boolean {
  return /Android/i.test(navigator.userAgent || '');
}

function isMobileUa(): boolean {
  return /Android|iPhone|iPad|iPod/i.test(navigator.userAgent || '');
}

export type SoftphoneRegStatus = 'idle' | 'connecting' | 'registered' | 'unregistered' | 'error';
export type SoftphoneCallPhase = 'idle' | 'ringing' | 'calling' | 'active' | 'ended';
export type SoftphoneLogLevel = 'info' | 'warn' | 'error' | 'ok';

export interface SoftphoneLogEntry {
  id: number;
  at: number;
  level: SoftphoneLogLevel;
  message: string;
}

export interface SoftphoneActiveCall {
  id: string;
  direction: 'in' | 'out';
  peer: string;
  phase: SoftphoneCallPhase;
  kind: 'doorbell' | 'phone';
  muted: boolean;
  speaker: boolean;
  startedAt: number;
  /** Esta llamada *puede* tener video (interno SHC, no timbre/PSTN). No implica UI de video. */
  wantsVideo?: boolean;
  /** El usuario abrió la vista de videollamada (no se abre al atender). */
  videoUiOn?: boolean;
  cameraOn?: boolean;
  facingUser?: boolean;
  hasRemoteVideo?: boolean;
}

type Listener = () => void;

const MAX_LOGS = 120;

function buildWsUrl(websocketPath: string): string {
  const path = (websocketPath || '/ucm-webrtc/ws').startsWith('/')
    ? (websocketPath || '/ucm-webrtc/ws')
    : `/${websocketPath}`;
  const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${proto}//${window.location.host}${path}`;
}

function peerFromUri(uri: string | undefined | null): string {
  if (!uri) return '';
  const raw = String(uri);
  const m = raw.match(/sip:([^@;>]+)/i);
  if (m) return m[1];
  return raw.replace(/^sip:/i, '').split('@')[0] || raw;
}

function errMsg(e: unknown): string {
  if (e == null) return 'error desconocido';
  if (typeof e === 'string') return e;
  const any = e as { message?: string; response?: { data?: { detail?: string }; status?: number } };
  const detail = any?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (any?.message) return any.message;
  return String(e);
}

/** Quita líneas vacías y fuerza CRLF (Chrome: "Invalid SDP line" con blanks). */
function normalizeSdpLines(lines: string[]): string {
  return `${lines.filter((l) => l.length > 0).join('\r\n')}\r\n`;
}

/**
 * SoftPhone = audio SIP only (video timbre = MediaMTX).
 *
 * Offer remoto: NO mutilar m=video a puerto 0 (dejaba SDP inválido / UCM 488).
 *   → Arreglar H.264 (profile-level-id → 42e01f) para setRemoteDescription.
 * Answer local: rechazar video con puerto 0 (misma cantidad de m-lines).
 * Audio: sacar G.726/G.729/… conflictivos.
 */
function fixH264ForChrome(lines: string[]): { lines: string[]; fixed: boolean } {
  let fixed = false;
  const out = lines.map((line) => {
    const m = line.match(/^a=fmtp:(\d+)\s+(.*)$/i);
    if (!m) return line;
    const pt = m[1];
    const rest = m[2];
    if (!/profile-level-id=/i.test(rest) && !/packetization-mode=/i.test(rest)) {
      return line;
    }
    fixed = true;
    let next = rest;
    if (/profile-level-id=/i.test(next)) {
      next = next.replace(/profile-level-id=[0-9a-fA-F]+/i, 'profile-level-id=42e01f');
    } else {
      next = `${next};profile-level-id=42e01f`;
    }
    if (!/packetization-mode=/i.test(next)) {
      next = `${next};packetization-mode=1`;
    } else {
      next = next.replace(/packetization-mode=\d+/i, 'packetization-mode=1');
    }
    return `a=fmtp:${pt} ${next}`;
  });
  return { lines: out, fixed };
}

function rejectVideoInAnswer(lines: string[]): { lines: string[]; rejected: boolean } {
  const rejectedMids: string[] = [];
  const out: string[] = [];
  let inVideo = false;
  let rejected = false;

  for (const line of lines) {
    if (line.startsWith('m=')) {
      inVideo = /^m=video\b/i.test(line);
      if (inVideo) {
        rejected = true;
        const parts = line.split(/\s+/).filter(Boolean);
        const proto = parts[2] || 'RTP/AVP';
        out.push(`m=video 0 ${proto} 0`);
        continue;
      }
    }
    if (inVideo) {
      if (/^a=mid:/i.test(line)) {
        rejectedMids.push(line.replace(/^a=mid:/i, '').trim());
        out.push(line);
      } else if (/^c=/i.test(line)) {
        out.push(line);
      }
      continue;
    }
    out.push(line);
  }

  if (!rejected) return { lines, rejected: false };

  const fixed = out
    .map((line) => {
      const m = line.match(/^(a=group:BUNDLE)\s+(.+)$/i);
      if (!m) return line;
      const mids = m[2].split(/\s+/).filter((mid) => mid && !rejectedMids.includes(mid));
      return mids.length ? `${m[1]} ${mids.join(' ')}` : '';
    })
    .filter((l) => l.length > 0);

  return { lines: fixed, rejected: true };
}

function stripBadAudioCodecs(lines: string[]): { lines: string[]; removed: string[] } {
  const removePts = new Set<string>();
  for (const line of lines) {
    const m = line.match(/^a=rtpmap:(\d+)\s+([^\s/]+)/i);
    if (!m) continue;
    const pt = m[1];
    const codec = m[2].toUpperCase();
    if (
      codec.startsWith('G726')
      || codec.startsWith('G729')
      || codec === 'GSM'
      || codec.startsWith('ILBC')
      || codec.startsWith('SPEEX')
      || codec.startsWith('G723')
    ) {
      removePts.add(pt);
    }
  }
  if (removePts.size === 0 && lines.some((l) => /^a=rtpmap:2\s+G726/i.test(l))) {
    removePts.add('2');
  }
  if (removePts.size === 0) return { lines, removed: [] };

  const out: string[] = [];
  let inAudio = false;
  for (const line of lines) {
    if (line.startsWith('m=')) {
      inAudio = line.startsWith('m=audio ');
      if (inAudio) {
        const parts = line.split(/\s+/).filter(Boolean);
        const head = parts.slice(0, 3);
        const fmts = parts.slice(3).filter((pt) => !removePts.has(pt));
        out.push([...head, ...(fmts.length ? fmts : ['0'])].join(' '));
        continue;
      }
    }
    if (inAudio) {
      let drop = false;
      for (const pt of removePts) {
        if (
          line.startsWith(`a=rtpmap:${pt} `)
          || line.startsWith(`a=fmtp:${pt} `)
          || line.startsWith(`a=fmtp:${pt};`)
          || line.startsWith(`a=rtcp-fb:${pt} `)
          || line === `a=rtcp-fb:${pt}`
        ) {
          drop = true;
          break;
        }
      }
      if (drop) continue;
    }
    out.push(line);
  }
  return { lines: out, removed: [...removePts] };
}

function sanitizeSdpForWebRtc(
  sdp: string,
  descType?: RTCSdpType,
  allowVideo = false,
): { sdp: string; notes: string[] } {
  const notes: string[] = [];
  let lines = sdp.split(/\r?\n/).filter((l) => l.length > 0);

  // Offer (y pranswer): reparar H264 para setRemoteDescription
  if (descType !== 'answer') {
    const h264 = fixH264ForChrome(lines);
    lines = h264.lines;
    if (h264.fixed) notes.push('H264 profile→42e01f');
  }

  // Answer: timbre / voz-only → rechazar m=video. Phone (Grandstream/PWA): dejar video
  // aunque no mandemos cámara (recvonly) para poder mostrarlo si el usuario activa video.
  if (descType === 'answer' && !allowVideo) {
    const vid = rejectVideoInAnswer(lines);
    lines = vid.lines;
    if (vid.rejected) notes.push('answer: m=video puerto 0');
  }

  const audio = stripBadAudioCodecs(lines);
  lines = audio.lines;
  if (audio.removed.length) notes.push(`audio PT fuera: ${audio.removed.join(',')}`);

  return { sdp: normalizeSdpLines(lines), notes };
}

export class SipSoftphone {
  private ua: UserAgent | null = null;
  private registerer: Registerer | null = null;
  private session: Session | null = null;
  private remoteAudio: HTMLAudioElement | null = null;
  private localVideoEl: HTMLVideoElement | null = null;
  private remoteVideoEl: HTMLVideoElement | null = null;
  /** SDP: true en phone (interno) para no tumbar m=video. Timbre siempre false. */
  private sdpAllowVideo = false;
  private cameraFacingUser = true;
  /**
   * Cámara nativa (preview) + track 16:9 con barras (lo que se manda al fijo).
   * El aparato Grandstream estira a pantalla ancha: si mandamos vertical, se deforma.
   */
  private videoPump: {
    cameraTrack: MediaStreamTrack;
    sendTrack: MediaStreamTrack;
    stop: () => void;
  } | null = null;
  private credentials: SoftphoneCredentials | null = null;
  private doorbellExt: string | null = null;
  private ringCtx: AudioContext | null = null;
  private ringTimer: ReturnType<typeof setInterval> | null = null;
  private ringOscStop: (() => void) | null = null;
  private ringDeadlineTimer: ReturnType<typeof setTimeout> | null = null;
  private ringTimeoutSec = 45;
  private reportedRingCallId: string | null = null;
  private visibilityHandler: (() => void) | null = null;
  private handoffRetryTimer: ReturnType<typeof setInterval> | null = null;
  private handoffRetryKickTimer: ReturnType<typeof setTimeout> | null = null;
  /** Tras transfer OK no volver a POST /ready (el hold ya no existe). */
  private handoffTransferDone = false;
  private handoffTransferredAt = 0;
  private startInFlight = false;
  private warmedMic: MediaStream | null = null;
  private warmedMicIdleTimer: ReturnType<typeof setTimeout> | null = null;
  /** Pre-warm en Teléfono (móvil): soltar si no hay llamada. */
  private static readonly WARMED_MIC_IDLE_MS = 10000;
  /** El usuario ya tocó Atender: accept() al llegar el INVITE. */
  private pendingAccept = false;
  /** Cámara del timbre (config / credentials). */
  cameraId: number | null = null;
  /** Quiere quedarse registrado (hasta stop explícito). */
  private wantsRegistered = false;
  private reconnectFallbackTimer: ReturnType<typeof setTimeout> | null = null;
  private reregisterInFlight = false;
  /** Keep-alive CRLF en el WS SIP (segundos). 0 = off en SIP.js; sin esto UCM corta idle → 1006. */
  private static readonly WS_KEEPALIVE_SEC = 25;
  /** Watchdog: si no estamos registered más de esto, fuerza reregister(). */
  private static readonly REG_STUCK_MS = 20000;
  private static readonly REG_WATCHDOG_TICK_MS = 10000;
  /** Solo INVITE saliente: no esperar TURN hasta el timeout global de 5s (atender no usa esto). */
  private static readonly OUTBOUND_ICE_GATHER_MS = 1500;
  private regWatchdogInterval: ReturnType<typeof setInterval> | null = null;
  private notRegisteredSince: number | null = null;

  regStatus: SoftphoneRegStatus = 'idle';
  regError: string | null = null;
  extension: string | null = null;
  /** No molestar: rechaza INVITE sin UI / sin reportar ringing (legacy bool = manual). */
  dnd: boolean = false;
  dndManualScope: SoftphoneDndScope = 'both';
  dndRules: SoftphoneDndRule[] = [];
  serverTimezone: string = 'America/Argentina/Buenos_Aires';
  dndEffective: SoftphoneDndEffective = {
    active: false,
    source: null,
    color: null,
  };
  /** true mientras corre session.accept() — evita falso “llamada finalizada”. */
  acceptInFlight: boolean = false;
  activeCall: SoftphoneActiveCall | null = null;
  logs: SoftphoneLogEntry[] = [];
  private logSeq = 0;

  private listeners = new Set<Listener>();

  subscribe(fn: Listener): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  setDnd(enabled: boolean) {
    this.dnd = Boolean(enabled);
    this.refreshDndEffective();
    this.pushLog('info', enabled ? 'No molestar ON — no ring/push' : 'No molestar OFF');
    this.emit();
  }

  applyDndSettings(opts: {
    manualEnabled: boolean;
    manualScope?: SoftphoneDndScope;
    rules?: SoftphoneDndRule[];
    serverTimezone?: string;
    effective?: SoftphoneDndEffective;
  }) {
    this.dnd = Boolean(opts.manualEnabled);
    this.dndManualScope = opts.manualScope || 'both';
    this.dndRules = Array.isArray(opts.rules) ? opts.rules : [];
    if (opts.serverTimezone) this.serverTimezone = opts.serverTimezone;
    if (opts.effective) {
      this.dndEffective = opts.effective;
    } else {
      this.refreshDndEffective();
    }
    this.emit();
  }

  private refreshDndEffective() {
    this.dndEffective = headerIndicator({
      manualEnabled: this.dnd,
      manualScope: this.dndManualScope,
      rules: this.dndRules,
      serverTimezone: this.serverTimezone,
    });
  }

  /** true si este kind no debe sonar / publicarse. */
  blocksKind(kind: 'doorbell' | 'phone' | string): boolean {
    return evaluateLocalDnd({
      manualEnabled: this.dnd,
      manualScope: this.dndManualScope,
      rules: this.dndRules,
      kind,
      serverTimezone: this.serverTimezone,
    }).blocked;
  }

  /** Timbre si el peer coincide con `doorbell_sip_extension`; si no, teléfono. */
  private classifyKind(peer: string): 'doorbell' | 'phone' {
    const p = (peer || '').trim();
    const door = (this.doorbellExt || '').trim();
    if (door && p && p === door) return 'doorbell';
    return 'phone';
  }

  isDoorbellDest(dest: string): boolean {
    return this.classifyKind(dest) === 'doorbell';
  }

  /** Video del dispositivo: phone + interno corto (3–5 dígitos), no timbre ni PSTN. */
  wantsDeviceVideo(kind: string, peer: string): boolean {
    if (kind !== 'phone') return false;
    if (this.classifyKind(peer) === 'doorbell') return false;
    const digits = String(peer || '').replace(/\D/g, '');
    if (!digits || digits.length < 3 || digits.length > 5) return false;
    return true;
  }

  private emit() {
    this.listeners.forEach((fn) => {
      try { fn(); } catch { /* ignore */ }
    });
  }

  private pushLog(level: SoftphoneLogLevel, message: string) {
    this.logSeq += 1;
    this.logs = [
      ...this.logs,
      { id: this.logSeq, at: Date.now(), level, message },
    ].slice(-MAX_LOGS);
    this.emit();
  }

  clearLogs() {
    this.logs = [];
    this.emit();
  }

  /** Error al pedir /credentials (sin extensión, 403, etc.) — no llegó a SIP.js */
  noteBootstrapError(e: unknown) {
    const msg = errMsg(e);
    this.regStatus = 'error';
    this.regError = msg;
    this.pushLog('error', `Credenciales: ${msg}`);
    this.emit();
  }

  private ensureRemoteAudio(): HTMLAudioElement {
    if (!this.remoteAudio) {
      const el = document.createElement('audio');
      el.autoplay = true;
      el.setAttribute('playsinline', 'true');
      el.style.display = 'none';
      document.body.appendChild(el);
      this.remoteAudio = el;
    }
    return this.remoteAudio;
  }

  /** Desbloquear AudioContext tras un gesto del usuario (necesario en Chrome móvil). */
  unlockAudio(): void {
    try {
      const AC = window.AudioContext
        || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!AC) return;
      if (!this.ringCtx) this.ringCtx = new AC();
      if (this.ringCtx.state === 'suspended') {
        void this.ringCtx.resume().catch(() => { /* ignore */ });
      }
    } catch { /* ignore */ }
  }

  private stopRingtone(): void {
    if (this.ringTimer) {
      clearInterval(this.ringTimer);
      this.ringTimer = null;
    }
    if (this.ringOscStop) {
      try { this.ringOscStop(); } catch { /* ignore */ }
      this.ringOscStop = null;
    }
    try {
      navigator.vibrate?.(0);
    } catch { /* ignore */ }
    void import('../../utils/pushRingSound').then(({ stopPushRingSound }) => {
      stopPushRingSound();
    }).catch(() => { /* ignore */ });
  }

  /** Cortar tono/vibración in-app y cerrar banners de ring del SW (p. ej. al atender). */
  silenceIncomingRing(): void {
    this.stopRingtone();
    void this.dismissSoftphoneRingNotifications();
  }

  /**
   * Otro dispositivo atendió / CANCEL / timeout.
   * Timbre en preview: answered o CANCEL (Wave/otro) cierra la isla.
   * Timbre si ya tocamos Atender: CANCEL del Redirect se ignora.
   * @returns true si abortó una espera o llamada local en ringing.
   */
  handleRemoteCleared(
    _reason?: string,
    payload?: { call_id?: string | null },
  ): boolean {
    this.silenceIncomingRing();
    const reason = String(_reason || '').toLowerCase();
    const answered = reason === 'answered' || reason === 'answered_elsewhere';
    const ac = this.activeCall;
    // Sin llamada local todavía: solo cortar si OTRO atendió de verdad.
    // cancel/dismiss/timeout en esta espera son normales (la central pasa la llamada);
    // si los tomamos como “se acabó”, el botón se queda gris y nunca pasa a verde.
    if (!ac || ac.phase === 'ended') {
      if (answered) {
        this.stopHandoffRetries();
        this.pushLog('info', 'Espera timbre abortada (atendida en otro lado, aún sin llamada local)');
        this.emit();
        return true;
      }
      // Isla abierta sin INVITE local: Wave/otro atendió → CANCEL. Si nosotros
      // ya tocamos Atender, el CANCEL es del Redirect y la isla tiene que seguir.
      if (
        (reason === 'cancel' || reason === 'dismiss' || reason === 'timeout')
        && !this.isDoorbellClaimInProgress()
      ) {
        this.stopHandoffRetries();
        this.pushLog('info', `Isla timbre cerrada (${reason}, sin llamada local)`);
        this.emit();
        return true;
      }
      return false;
    }
    const phoneLive = ac.kind === 'phone'
      && (ac.phase === 'active' || ac.phase === 'calling' || ac.phase === 'ringing');
    // El peer colgó de verdad. NO incluir answered: ese evento lo manda
    // /calls/answered y mataba a quien acaba de atender y al que llama.
    const peerGone = reason === 'ended' || reason === 'missed' || reason === 'hangup' || reason === 'busy';
    if (phoneLive && peerGone) {
      this.pushLog('info', `Teléfono: corte remoto (${reason || 'ended'})`);
      void this.hangup();
      return true;
    }
    if (ac.direction !== 'in' || ac.phase !== 'ringing') return false;
    if (answered) {
      this.pushLog('info', 'Ring abortado (atendida en otro lado)');
      void this.hangup();
      return true;
    }
    if (reason === 'cancel' || reason === 'dismiss' || reason === 'timeout' || !reason) {
      if (ac.kind === 'doorbell' && !this.isDoorbellClaimInProgress()) {
        this.pushLog('info', `Timbre preview: corte remoto (${reason || 'remoto'}) — cierro isla`);
        void this.hangup();
        return true;
      }
      this.pushLog('info', `Clear remoto ignorado (${ac.kind} ${reason || 'sin reason'})`);
      return false;
    }
    const remoteId = String(payload?.call_id || '').trim();
    const localId = String(ac.id || '').trim();
    if (remoteId && localId && remoteId !== localId
      && !remoteId.startsWith(localId.slice(0, 16))
      && !localId.startsWith(remoteId.slice(0, 16))) {
      this.pushLog('info', `Clear remoto ignorado (otro call_id ${remoteId.slice(0, 12)}…)`);
      return false;
    }
    this.pushLog('info', `Ring abortado (${reason || 'remoto'})`);
    void this.hangup();
    return true;
  }

  private async dismissSoftphoneRingNotifications(): Promise<void> {
    try {
      if (!('serviceWorker' in navigator)) return;
      const reg = await navigator.serviceWorker.ready;
      const tags = ['shc-softphone-ring', 'shc-softphone-doorbell'];
      for (const tag of tags) {
        const notes = await reg.getNotifications({ tag });
        notes.forEach((n) => {
          try { n.close(); } catch { /* ignore */ }
        });
      }
    } catch { /* ignore */ }
  }

  private clearRingDeadline(): void {
    if (this.ringDeadlineTimer) {
      clearTimeout(this.ringDeadlineTimer);
      this.ringDeadlineTimer = null;
    }
  }

  private armRingDeadline(): void {
    this.clearRingDeadline();
    const sec = Math.max(10, Number(this.ringTimeoutSec) || 45);
    this.ringDeadlineTimer = setTimeout(() => {
      if (this.activeCall?.phase === 'ringing' && this.activeCall.direction === 'in') {
        this.pushLog('warn', `Ring timeout ${sec}s — corto tono y rechazo`);
        this.stopRingtone();
        void this.hangup();
      }
    }, sec * 1000);
  }

  private startRingtone(): void {
    this.stopRingtone();
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
      this.pushLog('info', 'Sin tono: app en background (abrí SHC para atender)');
      return;
    }
    this.unlockAudio();

    const ctx = this.ringCtx;
    const playBurst = () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
        this.stopRingtone();
        return;
      }
      try {
        if (typeof navigator.vibrate === 'function') {
          navigator.vibrate([400, 200, 400, 200, 400, 600]);
        }
      } catch { /* ignore */ }

      if (!ctx) return;
      if (ctx.state === 'suspended') {
        void ctx.resume().catch(() => { /* ignore */ });
      }
      try {
        const now = ctx.currentTime;
        const stops: Array<() => void> = [];
        [880, 1046].forEach((freq, i) => {
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.type = 'sine';
          osc.frequency.value = freq;
          const t0 = now + i * 0.4;
          gain.gain.setValueAtTime(0.0001, t0);
          gain.gain.exponentialRampToValueAtTime(0.2, t0 + 0.02);
          gain.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.35);
          osc.connect(gain);
          gain.connect(ctx.destination);
          osc.start(t0);
          osc.stop(t0 + 0.38);
          stops.push(() => {
            try { osc.disconnect(); gain.disconnect(); } catch { /* ignore */ }
          });
        });
        this.ringOscStop = () => stops.forEach((fn) => fn());
      } catch { /* ignore */ }
    };

    playBurst();
    this.ringTimer = setInterval(playBurst, 2000);
    this.pushLog('info', ctx ? 'Ring entrante (tono + vibración)' : 'Ring entrante (solo vibración; tocá la app para habilitar audio)');
  }

  /** Avisa a la PWA (navegar a Teléfono/Video) + push/SIO vía API. */
  private publishIncoming(kind: 'doorbell' | 'phone', peer: string): void {
    const callId = this.activeCall?.id;
    const payload = {
      call_id: callId,
      kind,
      from: peer,
      camera_id: this.credentials?.camera_id ?? null,
    };
    try {
      eventBus.emit('softphone:incoming', payload);
    } catch { /* ignore */ }

    if (!callId || this.reportedRingCallId === callId) return;
    this.reportedRingCallId = callId;
    void import('./softphoneApi').then(({ reportSoftphoneRinging }) => {
      reportSoftphoneRinging({
        call_id: callId,
        from: peer,
        kind,
      }).catch(() => { /* ignore */ });
    });
  }

  /**
   * Avisar al server que el WebRTC está registrado.
   * Sin claim: timbre NO hace Redirect (el ring group sigue).
   * Con claim: el usuario tocó Atender → AMI Redirect + INVITE a esta PWA.
   */
  async notifyServerReady(opts?: { claim?: boolean; reclaim?: boolean }): Promise<{
    transferred?: boolean;
    reason?: string;
    call_id?: string;
  } | null> {
    if (this.regStatus !== 'registered') return null;
    const claim = Boolean(opts?.claim);
    const reclaim = Boolean(opts?.reclaim);
    try {
      const { reportSoftphoneReady } = await import('./softphoneApi');
      const data = await reportSoftphoneReady({ claim, reclaim });
      if (data?.transferred) {
        this.pushLog('ok', `Handoff: transfer OK call=${String(data.call_id || '').slice(0, 12)}… — esperá ring`);
        this.handoffTransferDone = true;
        this.handoffTransferredAt = Date.now();
      } else if (data?.reason === 'waiting_user_answer') {
        this.pushLog('info', 'Handoff: timbre en espera — Redirect solo al tocar Atender');
      } else if (data?.reason === 'webrtc_native_invite') {
        this.pushLog('info', 'Handoff: peek omitido — INVITE nativo WebRTC');
      } else if (data?.reason === 'already_transferring') {
        this.handoffTransferDone = true;
        this.pushLog('info', 'Handoff: already_transferring — esperá INVITE');
      } else if (data?.reason === 'ami_redirect_failed') {
        this.pushLog('warn', 'Handoff: AMI redirect falló — reintento (hold intacto)');
      } else if (data?.reason === 'reclaimed') {
        this.pushLog('ok', 'Handoff: reclaim Redirect — esperá ring en esta ventana');
      } else if (data?.reason && data.reason !== 'no_held_call') {
        this.pushLog('info', `Handoff: ${data.reason}`);
      }
      return data || null;
    } catch (e: unknown) {
      this.pushLog('warn', `Handoff ready falló: ${errMsg(e)}`);
      return null;
    }
  }

  /**
   * Quick-answer: esperar REGISTER, pedir ready **una vez** (o hasta transfer OK),
   * luego solo esperar INVITE. No martillar /calls/ready (rompe el hold).
   */
  startHandoffRetries(opts?: { intervalMs?: number; maxMs?: number }): void {
    this.stopHandoffRetries();
    this.handoffTransferDone = false;
    this.handoffTransferredAt = 0;
    const intervalMs = Math.max(400, opts?.intervalMs ?? 800);
    const maxMs = Math.max(intervalMs, opts?.maxMs ?? 60000);
    const started = Date.now();
    this.pushLog('info', `Handoff: wait REGISTER/INVITE (máx ${maxMs}ms)`);

    const tick = () => {
      if (this.activeCall
        && (this.activeCall.phase === 'ringing'
          || this.activeCall.phase === 'active'
          || this.activeCall.phase === 'calling')) {
        this.stopHandoffRetries();
        return;
      }
      if (Date.now() - started >= maxMs) {
        this.pushLog('warn', 'Handoff: sin INVITE a tiempo');
        this.stopHandoffRetries();
        this.emit();
        return;
      }
      if (this.regStatus !== 'registered') {
        if (this.regStatus === 'connecting' || this.startInFlight) return;
        void this.reregister();
        return;
      }
      // Transfer OK: esperar INVITE. Si no llega, reintentar ready
      // (puede haber un hold UDP nuevo porque la UCM no pegó al WS).
      if (this.handoffTransferDone) {
        if (this.handoffTransferredAt && Date.now() - this.handoffTransferredAt < 800) {
          return;
        }
        this.handoffTransferDone = false;
        this.pushLog('warn', 'Handoff: sin INVITE tras transfer — reintento ready');
      }
      void this.notifyServerReady({ claim: true }).then((data) => {
        if (data?.transferred || data?.reason === 'already_transferring') {
          this.handoffTransferDone = true;
          this.handoffTransferredAt = Date.now();
        }
      });
    };

    this.handoffRetryTimer = setInterval(tick, intervalMs);
    this.handoffRetryKickTimer = setTimeout(tick, 50);
  }

  stopHandoffRetries(): void {
    if (this.handoffRetryTimer) {
      clearInterval(this.handoffRetryTimer);
      this.handoffRetryTimer = null;
    }
    if (this.handoffRetryKickTimer) {
      clearTimeout(this.handoffRetryKickTimer);
      this.handoffRetryKickTimer = null;
    }
  }

  /** Esta PWA tocó Atender (claim / accept). Un CANCEL del ring group no debe cerrar la isla. */
  private isDoorbellClaimInProgress(): boolean {
    if (this.pendingAccept || this.acceptInFlight) return true;
    if (this.handoffRetryTimer || this.handoffRetryKickTimer) return true;
    return false;
  }

  private bindVisibilityLifecycle(): void {
    if (typeof document === 'undefined' || this.visibilityHandler) return;
    this.visibilityHandler = () => {
      if (document.visibilityState === 'hidden') {
        this.stopRingtone();
        this.pushLog('info', 'Background: tono/vibración off');
        return;
      }
      this.unlockAudio();
      if (this.activeCall?.phase === 'ringing' && this.activeCall.direction === 'in') {
        this.publishIncoming(this.activeCall.kind, this.activeCall.peer);
        this.startRingtone();
      }
      if (this.regStatus === 'unregistered' || this.regStatus === 'error') {
        this.pushLog('info', 'Foreground: reintento REGISTER');
        void this.reregister();
      }
    };
    document.addEventListener('visibilitychange', this.visibilityHandler);
  }

  private unbindVisibilityLifecycle(): void {
    if (!this.visibilityHandler || typeof document === 'undefined') return;
    document.removeEventListener('visibilitychange', this.visibilityHandler);
    this.visibilityHandler = null;
  }

  private clearReconnectFallback(): void {
    if (this.reconnectFallbackTimer) {
      clearTimeout(this.reconnectFallbackTimer);
      this.reconnectFallbackTimer = null;
    }
  }

  private markNotRegistered(): void {
    if (this.notRegisteredSince === null) {
      this.notRegisteredSince = Date.now();
    }
  }

  private markRegisteredOk(): void {
    this.notRegisteredSince = null;
  }

  private startRegWatchdog(): void {
    if (this.regWatchdogInterval) return;
    this.regWatchdogInterval = setInterval(() => {
      try {
        if (!this.wantsRegistered) {
          this.notRegisteredSince = null;
          return;
        }
        if (this.regStatus === 'registered') {
          this.markRegisteredOk();
          return;
        }
        if (this.regStatus === 'idle') return;
        this.markNotRegistered();
        if (this.reregisterInFlight) return;
        if (
          this.activeCall
          && (this.activeCall.phase === 'active'
            || this.activeCall.phase === 'calling'
            || this.activeCall.phase === 'ringing')
        ) {
          return;
        }
        const stuckMs = Date.now() - (this.notRegisteredSince || Date.now());
        if (stuckMs < SipSoftphone.REG_STUCK_MS) return;
        this.pushLog(
          'warn',
          `Watchdog registro SIP: ${this.regStatus} > ${Math.round(stuckMs / 1000)}s — reintento completo`,
        );
        // Evitar martillar: reinicia el cronómetro; el próximo tick vuelve a medir.
        this.notRegisteredSince = Date.now();
        void this.reregister();
      } catch {
        /* el watchdog nunca debe romper SoftPhone */
      }
    }, SipSoftphone.REG_WATCHDOG_TICK_MS);
  }

  private stopRegWatchdog(): void {
    if (this.regWatchdogInterval) {
      clearInterval(this.regWatchdogInterval);
      this.regWatchdogInterval = null;
    }
    this.notRegisteredSince = null;
  }

  /** Si el transport no vuelve solo, reinicio completo del UA. */
  private scheduleReconnectFallback(delayMs = 8000): void {
    if (!this.wantsRegistered) return;
    this.clearReconnectFallback();
    this.reconnectFallbackTimer = setTimeout(() => {
      this.reconnectFallbackTimer = null;
      if (!this.wantsRegistered) return;
      if (this.regStatus === 'registered') return;
      if (this.regStatus === 'connecting' && this.startInFlight) return;
      if (this.activeCall && (this.activeCall.phase === 'active' || this.activeCall.phase === 'calling')) {
        // Esperar a que termine la llamada; reintentar después
        this.scheduleReconnectFallback(5000);
        return;
      }
      this.pushLog('warn', 'WebSocket caído — reintento completo de registro');
      void this.reregister();
    }, delayMs);
  }

  private async ensureRegisteredAfterConnect(): Promise<void> {
    if (!this.wantsRegistered || !this.registerer) return;
    try {
      this.pushLog('info', 'WebSocket OK — renovando REGISTER…');
      this.regStatus = 'connecting';
      this.markNotRegistered();
      this.emit();
      await this.registerer.register();
    } catch (e: unknown) {
      this.pushLog('warn', `REGISTER tras reconnect falló: ${errMsg(e)}`);
      this.scheduleReconnectFallback(5000);
    }
  }

  private setupSession(session: Session, direction: 'in' | 'out', peer: string, kind?: 'doorbell' | 'phone') {
    this.stopHandoffRetries();
    this.handoffTransferDone = false;
    this.session = session;
    let callKind = kind || this.classifyKind(peer);
    // Isla timbre: el INVITE post-Atender es de esta llamada aunque el peer
    // no matchee doorbell_sip_extension. No usar isSoftphoneQuickAnswer():
    // answer=1 también lo usan llamadas de teléfono (1001→1005).
    if (callKind !== 'doorbell' && direction === 'in' && isDoorbellIncomingContext()) {
      callKind = 'doorbell';
    }
    const wantsVideo = this.wantsDeviceVideo(callKind, peer);
    this.activeCall = {
      id: session.id || `call-${Date.now()}`,
      direction,
      peer,
      phase: direction === 'in' ? 'ringing' : 'calling',
      kind: callKind,
      wantsVideo,
      videoUiOn: false,
      cameraOn: false,
      facingUser: true,
      hasRemoteVideo: false,
      muted: false,
      speaker: callKind === 'doorbell',
      startedAt: Date.now(),
    };
    this.applySpeaker(this.activeCall.speaker);
    this.pushLog('info', `Llamada ${direction} ${callKind} peer=${peer}`);
    this.emit();
    if (direction === 'in') {
      this.publishIncoming(callKind, peer);
      // Timbre y teléfono: tono/vibración hasta atender o colgar (también con PWA en primer plano).
      this.startRingtone();
      this.armRingDeadline();
    }

    session.stateChange.addListener((state: SessionState) => {
      if (!this.activeCall) return;
      if (state === SessionState.Established) {
        this.stopRingtone();
        this.clearRingDeadline();
        this.activeCall = { ...this.activeCall, phase: 'active' };
        this.attachRemoteMedia(session);
        if (this.activeCall.kind === 'phone') {
          this.attachLocalPreview();
        }
        void this.applySpeaker(this.activeCall.speaker);
        this.pushLog('ok', 'Llamada establecida');
        this.emit();
      } else if (
        state === SessionState.Terminated
        || state === SessionState.Terminating
      ) {
        this.stopRingtone();
        this.clearRingDeadline();
        this.activeCall = { ...this.activeCall, phase: 'ended' };
        this.pushLog('info', 'Llamada terminada');
        this.emit();
        this.cleanupSession();
      }
    });
  }

  private attachRemoteMedia(session: Session) {
    try {
      const pc = getPeerConnection(session);
      if (!pc) return;
      const remote = this.ensureRemoteAudio();
      const doorbell = this.activeCall?.kind === 'doorbell';
      // Timbre: mismo camino que antes del video SIP (todas las tracks al <audio>).
      if (doorbell) {
        const stream = new MediaStream();
        pc.getReceivers().forEach((r) => {
          if (r.track) stream.addTrack(r.track);
        });
        remote.srcObject = stream;
        void remote.play().catch(() => { /* autoplay policy */ });
        this.applySpeaker(this.activeCall?.speaker ?? false);
        return;
      }
      const rawPc = (session.sessionDescriptionHandler as { peerConnection?: RTCPeerConnection } | undefined)
        ?.peerConnection;
      if (rawPc && !rawPc.ontrack) {
        rawPc.ontrack = () => this.attachRemoteMedia(session);
      }
      const audioStream = new MediaStream();
      const videoStream = new MediaStream();
      let hasVideo = false;
      pc.getReceivers().forEach((r) => {
        if (!r.track) return;
        if (r.track.kind === 'video') {
          videoStream.addTrack(r.track);
          hasVideo = r.track.readyState !== 'ended';
          r.track.onunmute = () => {
            if (this.session) this.attachRemoteMedia(this.session);
          };
        } else {
          audioStream.addTrack(r.track);
        }
      });
      remote.srcObject = audioStream;
      void remote.play().catch(() => { /* autoplay policy */ });
      if (this.remoteVideoEl) {
        this.remoteVideoEl.srcObject = hasVideo ? videoStream : null;
        if (hasVideo) void this.remoteVideoEl.play().catch(() => { /* autoplay */ });
      }
      if (this.activeCall && this.activeCall.hasRemoteVideo !== hasVideo) {
        this.activeCall = { ...this.activeCall, hasRemoteVideo: hasVideo };
        this.emit();
      }
      this.applySpeaker(this.activeCall?.speaker ?? false);
    } catch {
      /* ignore */
    }
  }

  private attachLocalPreview() {
    const el = this.localVideoEl;
    if (!el) return;
    try {
      const cam = this.videoPump?.cameraTrack;
      if (cam && cam.readyState === 'live') {
        el.srcObject = new MediaStream([cam]);
        el.muted = true;
        el.setAttribute('playsinline', 'true');
        void el.play().catch(() => { /* autoplay */ });
        return;
      }
      if (!this.session) {
        el.srcObject = null;
        return;
      }
      const pc = getPeerConnection(this.session);
      const stream = new MediaStream();
      pc?.getSenders().forEach((s) => {
        if (s.track && s.track.kind === 'video' && s.track.readyState === 'live') {
          stream.addTrack(s.track);
        }
      });
      el.srcObject = stream.getVideoTracks().length ? stream : null;
      el.muted = true;
      el.setAttribute('playsinline', 'true');
      void el.play().catch(() => { /* autoplay */ });
    } catch {
      /* ignore */
    }
  }

  bindVideoElements(local: HTMLVideoElement | null, remote: HTMLVideoElement | null) {
    this.localVideoEl = local;
    this.remoteVideoEl = remote;
    if (remote) {
      remote.muted = true;
      remote.setAttribute('playsinline', 'true');
    }
    if (local) {
      local.muted = true;
      local.setAttribute('playsinline', 'true');
    }
    if (this.session) {
      this.attachRemoteMedia(this.session);
      this.attachLocalPreview();
    }
  }

  private getRawPeerConnection(): RTCPeerConnection | null {
    const sdh = this.session?.sessionDescriptionHandler as { peerConnection?: RTCPeerConnection } | undefined;
    return sdh?.peerConnection ?? null;
  }

  private findVideoSender(raw: RTCPeerConnection): RTCRtpSender | undefined {
    const byTrack = raw.getSenders().find((s) => s.track?.kind === 'video');
    if (byTrack) return byTrack;
    const trx = raw.getTransceivers().find((t) => (
      t.receiver.track?.kind === 'video'
      || t.sender.track?.kind === 'video'
    ));
    return trx?.sender;
  }

  /**
   * Vista de video: off por defecto al atender. El botón de cámara la prende/apaga.
   * Al prender: UI grande + cámara local (si hay permiso) + altavoz.
   * Al apagar: cámara off y vuelve la UI de voz/teclado.
   */
  async setVideoUi(on: boolean): Promise<void> {
    if (!this.activeCall || this.activeCall.kind !== 'phone') return;
    if (!this.wantsDeviceVideo(this.activeCall.kind, this.activeCall.peer)) return;
    if (on) {
      this.sdpAllowVideo = true;
      this.activeCall = { ...this.activeCall, videoUiOn: true, speaker: true };
      this.applySpeaker(true);
      this.emit();
      await this.setCameraEnabled(true);
      if (this.session) this.attachRemoteMedia(this.session);
      return;
    }
    await this.setCameraEnabled(false);
    if (!this.activeCall) return;
    this.activeCall = { ...this.activeCall, videoUiOn: false, speaker: false };
    this.applySpeaker(false);
    this.emit();
  }

  private stopVideoPump(): void {
    if (!this.videoPump) return;
    try { this.videoPump.stop(); } catch { /* ignore */ }
    this.videoPump = null;
  }

  private async acquireCameraTrack(facingUser: boolean): Promise<MediaStreamTrack> {
    const labeled = (await this.listVideoInputs()).filter((d) => d.deviceId);
    const wantRe = facingUser
      ? /front|user|facing front|delantera/i
      : /back|rear|environment|trasera|facing back/i;
    const byLabel = labeled.find((d) => wantRe.test(d.label || ''));
    const tries: Array<MediaTrackConstraints | true> = [];
    if (byLabel) tries.push({ deviceId: { exact: byLabel.deviceId } });
    tries.push({ facingMode: { ideal: facingUser ? 'user' : 'environment' } });
    tries.push(true);
    let lastErr: unknown;
    for (const video of tries) {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: false,
          video: video === true ? true : video,
        });
        const track = stream.getVideoTracks()[0];
        if (track) {
          stream.getTracks().forEach((t) => { if (t !== track) t.stop(); });
          return track;
        }
        stream.getTracks().forEach((t) => t.stop());
      } catch (e) {
        lastErr = e;
      }
    }
    throw lastErr || new Error('sin cámara');
  }

  private async listVideoInputs(): Promise<MediaDeviceInfo[]> {
    if (!navigator.mediaDevices?.enumerateDevices) return [];
    let cams = (await navigator.mediaDevices.enumerateDevices()).filter((d) => d.kind === 'videoinput');
    if (!cams.some((c) => Boolean(c.label)) && navigator.mediaDevices.getUserMedia) {
      try {
        const tmp = await navigator.mediaDevices.getUserMedia({ audio: false, video: true });
        tmp.getTracks().forEach((t) => t.stop());
        cams = (await navigator.mediaDevices.enumerateDevices()).filter((d) => d.kind === 'videoinput');
      } catch {
        /* sin permiso */
      }
    }
    return cams;
  }

  /** Enmarca el vertical en 16:9 (barras negras). El fijo estira; así no deforma la cara. */
  private makeLandscapePump(cameraTrack: MediaStreamTrack): {
    cameraTrack: MediaStreamTrack;
    sendTrack: MediaStreamTrack;
    stop: () => void;
  } {
    const video = document.createElement('video');
    video.muted = true;
    video.playsInline = true;
    video.setAttribute('playsinline', 'true');
    video.srcObject = new MediaStream([cameraTrack]);
    video.style.cssText = 'position:fixed;width:2px;height:2px;opacity:0;pointer-events:none;left:0;top:0';
    document.body.appendChild(video);

    const canvas = document.createElement('canvas');
    canvas.width = 960;
    canvas.height = 540;
    const ctx = canvas.getContext('2d', { alpha: false });
    if (!ctx) throw new Error('canvas 2d');

    let raf = 0;
    let last = 0;
    const draw = (ts: number) => {
      raf = requestAnimationFrame(draw);
      if (ts - last < 66) return;
      last = ts;
      const w = canvas.width;
      const h = canvas.height;
      ctx.fillStyle = '#000';
      ctx.fillRect(0, 0, w, h);
      const vw = video.videoWidth;
      const vh = video.videoHeight;
      if (!vw || !vh) return;
      const scale = Math.min(w / vw, h / vh);
      const dw = vw * scale;
      const dh = vh * scale;
      ctx.drawImage(video, (w - dw) / 2, (h - dh) / 2, dw, dh);
    };

    const out = canvas.captureStream(15);
    const sendTrack = out.getVideoTracks()[0];
    if (!sendTrack) throw new Error('sin track canvas');
    try { sendTrack.contentHint = 'motion'; } catch { /* ignore */ }

    const startDraw = () => {
      if (raf) return;
      raf = requestAnimationFrame(draw);
    };
    void video.play().then(startDraw).catch(startDraw);
    if (video.readyState >= 2) startDraw();
    else video.addEventListener('loadedmetadata', startDraw, { once: true });

    return {
      cameraTrack,
      sendTrack,
      stop: () => {
        if (raf) cancelAnimationFrame(raf);
        try { video.pause(); video.srcObject = null; video.remove(); } catch { /* ignore */ }
        try { sendTrack.stop(); } catch { /* ignore */ }
        try { cameraTrack.stop(); } catch { /* ignore */ }
      },
    };
  }

  private async attachSendTrack(track: MediaStreamTrack, raw: RTCPeerConnection): Promise<boolean> {
    const current = this.findVideoSender(raw);
    if (current?.replaceTrack) {
      await current.replaceTrack(track);
      const trx = raw.getTransceivers().find((t) => t.sender === current);
      if (trx && trx.direction !== 'sendrecv') {
        try { trx.direction = 'sendrecv'; } catch { /* ignore */ }
        return true;
      }
      return false;
    }
    try {
      raw.addTrack(track);
    } catch {
      raw.addTransceiver(track, { direction: 'sendrecv' });
    }
    return true;
  }

  /** Nueva cámara + 16:9. Si falla, deja la anterior (no apaga el video). */
  private async waitCameraTrackLive(track: MediaStreamTrack, ms: number): Promise<void> {
    const wide = Number(track.getSettings().width || 0);
    if (wide > 0 && !track.muted) return;
    await new Promise<void>((resolve) => {
      const done = () => {
        try { track.removeEventListener('unmute', done); } catch { /* ignore */ }
        window.clearTimeout(tid);
        resolve();
      };
      const tid = window.setTimeout(done, ms);
      try { track.addEventListener('unmute', done, { once: true }); } catch { done(); }
    });
  }

  private async applyCameraFacing(facingUser: boolean): Promise<void> {
    const raw = this.getRawPeerConnection();
    if (!raw) throw new Error('sin PeerConnection');
    const cam = await this.acquireCameraTrack(facingUser);
    await this.waitCameraTrackLive(cam, 1500);
    const camW = Number(cam.getSettings().width || 0);
    if (!camW) {
      this.pushLog('warn', 'Cámara sin frames — envío track crudo');
      const needReinvite = await this.attachSendTrack(cam, raw);
      const prev = this.videoPump;
      this.videoPump = {
        cameraTrack: cam,
        sendTrack: cam,
        stop: () => { try { cam.stop(); } catch { /* ignore */ } },
      };
      if (prev) {
        try { prev.stop(); } catch { /* ignore */ }
      }
      if (needReinvite) {
        try { await this.reinviteWithVideo(); } catch { /* ignore */ }
      }
      return;
    }
    let pump: ReturnType<SipSoftphone['makeLandscapePump']>;
    try {
      pump = this.makeLandscapePump(cam);
    } catch (e) {
      try { cam.stop(); } catch { /* ignore */ }
      throw e;
    }
    try {
      const needReinvite = await this.attachSendTrack(pump.sendTrack, raw);
      const prev = this.videoPump;
      this.videoPump = pump;
      if (prev) {
        try { prev.stop(); } catch { /* ignore */ }
      }
      if (needReinvite) {
        try { await this.reinviteWithVideo(); } catch { /* ya hay track 16:9 */ }
      }
    } catch (e) {
      if (this.videoPump !== pump) {
        try { pump.stop(); } catch { /* ignore */ }
      }
      throw e;
    }
  }

  async setCameraEnabled(on: boolean): Promise<void> {
    if (!this.session || !this.activeCall || this.activeCall.kind !== 'phone') return;
    const raw = this.getRawPeerConnection();
    if (!on) {
      if (raw) {
        const current = this.findVideoSender(raw);
        if (current?.replaceTrack) {
          try { await current.replaceTrack(null); } catch { /* ignore */ }
        } else if (current?.track) {
          current.track.enabled = false;
        }
      }
      this.stopVideoPump();
      this.activeCall = { ...this.activeCall, cameraOn: false };
      this.attachLocalPreview();
      this.emit();
      return;
    }
    try {
      await this.applyCameraFacing(this.cameraFacingUser);
      this.activeCall = { ...this.activeCall, cameraOn: true, facingUser: this.cameraFacingUser };
      this.attachLocalPreview();
      this.pushLog('ok', 'Cámara encendida (envío 16:9)');
    } catch (e: unknown) {
      this.pushLog('warn', `Cámara: ${errMsg(e)}`);
      if (!this.videoPump) {
        this.activeCall = { ...this.activeCall, cameraOn: false };
      }
    }
    this.emit();
  }

  async flipCamera(): Promise<void> {
    if (!this.activeCall || this.activeCall.kind !== 'phone') return;
    const next = !this.cameraFacingUser;
    try {
      await this.applyCameraFacing(next);
      this.cameraFacingUser = next;
      this.activeCall = { ...this.activeCall, cameraOn: true, facingUser: next };
      this.attachLocalPreview();
      this.pushLog('ok', next ? 'Cámara frontal' : 'Cámara trasera');
      this.emit();
    } catch (e: unknown) {
      this.pushLog('warn', `Cambio de cámara: ${errMsg(e)} — se mantiene la anterior`);
      this.emit();
    }
  }

  private async reinviteWithVideo(): Promise<void> {
    if (!this.session || this.activeCall?.kind !== 'phone') return;
    this.sdpAllowVideo = true;
    const media = this.mediaOptions(true);
    this.pushLog('info', 're-INVITE video');
    await this.session.invite({
      sessionDescriptionHandlerModifiers: media.sessionDescriptionHandlerModifiers,
      sessionDescriptionHandlerOptions: { constraints: media.constraints },
    });
  }

  private applySpeaker(speaker: boolean) {
    const el = this.remoteAudio;
    if (el) el.muted = false;
    void this.routeAudioOutput(speaker);
  }

  /**
   * Oreja vs altavoz en PWA:
   * - Desktop: setSinkId si existe.
   * - Android Chrome: setSinkId NO existe. Workaround documentado: elegir el
   *   audioinput etiquetado "Speakerphone" (getUserMedia + replaceTrack) fuerza
   *   la salida a altavoz; el mic normal vuelve a oreja.
   * - iOS: el SO decide; no hay API web fiable.
   */
  private async routeAudioOutput(speaker: boolean): Promise<void> {
    const el = this.remoteAudio as (HTMLAudioElement & { setSinkId?: (id: string) => Promise<void> }) | null;
    const hasSinkId = Boolean(el && typeof el.setSinkId === 'function');

    if (hasSinkId && el?.setSinkId) {
      try {
        await el.setSinkId(speaker ? 'default' : 'communications');
        this.pushLog('ok', `Audio salida (setSinkId) → ${speaker ? 'altavoz/default' : 'communications'}`);
      } catch (e: unknown) {
        this.pushLog('warn', `setSinkId falló: ${errMsg(e)}`);
      }
    }

    // Android / móvil sin setSinkId: ruta por cambio de mic
    if (isAndroidUa() || (isMobileUa() && !hasSinkId)) {
      await this.applyMicRouteForSpeaker(speaker);
      return;
    }

    if (!hasSinkId && !isMobileUa()) {
      this.pushLog('warn', 'Oreja/altavoz: setSinkId no disponible en este navegador');
    }
  }

  private async listAudioInputs(): Promise<MediaDeviceInfo[]> {
    if (!navigator.mediaDevices?.enumerateDevices) return [];
    let mics = (await navigator.mediaDevices.enumerateDevices()).filter((d) => d.kind === 'audioinput');
    if (!mics.some((m) => Boolean(m.label)) && navigator.mediaDevices.getUserMedia) {
      try {
        const tmp = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
        tmp.getTracks().forEach((t) => t.stop());
        mics = (await navigator.mediaDevices.enumerateDevices()).filter((d) => d.kind === 'audioinput');
      } catch {
        /* sin permiso mic */
      }
    }
    return mics;
  }

  private pickMicForSpeaker(mics: MediaDeviceInfo[], speaker: boolean): MediaDeviceInfo | undefined {
    if (!mics.length) return undefined;
    const isSpk = (d: MediaDeviceInfo) => /speakerphone|speaker phone|altavoz|loudspeaker/i.test(d.label || '');
    if (speaker) {
      return mics.find(isSpk) || mics.find((d) => /speaker/i.test(d.label || ''));
    }
    // Oreja: cualquier mic que NO sea Speakerphone
    return (
      mics.find((d) => d.label && !isSpk(d) && /earpiece|phone|headset|default|micrófono|microphone/i.test(d.label))
      || mics.find((d) => d.label && !isSpk(d))
      || mics.find((d) => d.deviceId === 'default')
      || mics[0]
    );
  }

  private async applyMicRouteForSpeaker(speaker: boolean): Promise<void> {
    if (!this.session || !navigator.mediaDevices?.getUserMedia) return;
    try {
      const mics = await this.listAudioInputs();
      this.pushLog(
        'info',
        `Ruta Android/móvil — mics: ${mics.map((m) => m.label || m.deviceId.slice(0, 8)).join(' | ') || '(ninguno)'}`,
      );
      const target = this.pickMicForSpeaker(mics, speaker);
      if (!target) {
        this.pushLog(
          'warn',
          speaker
            ? 'No hay mic "Speakerphone". En este Android no se puede forzar altavoz por API web.'
            : 'No hay mic de oreja detectable.',
        );
        return;
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          deviceId: { exact: target.deviceId },
          echoCancellation: true,
          noiseSuppression: true,
        },
        video: false,
      });
      const newTrack = stream.getAudioTracks()[0];
      if (!newTrack) {
        stream.getTracks().forEach((t) => t.stop());
        return;
      }

      const pc = getPeerConnection(this.session);
      const sender = pc?.getSenders().find((s) => s.track?.kind === 'audio' || (!s.track && typeof s.replaceTrack === 'function'));
      if (sender?.replaceTrack) {
        const old = sender.track;
        await sender.replaceTrack(newTrack);
        // Mantener mute visual
        if (this.activeCall?.muted) newTrack.enabled = false;
        old?.stop();
        // Detener tracks extra del stream nuevo (solo usamos uno)
        stream.getTracks().forEach((t) => {
          if (t !== newTrack) t.stop();
        });
        this.pushLog(
          'ok',
          `Ruta ${speaker ? 'altavoz' : 'oreja'} vía mic "${target.label || target.deviceId.slice(0, 8)}"`,
        );
      } else {
        newTrack.stop();
        this.pushLog('warn', 'Sin sender de audio para replaceTrack (¿llamada aún no establecida?)');
      }
    } catch (e: unknown) {
      this.pushLog('error', `Fallo ruta mic/altavoz: ${errMsg(e)}`);
    }
  }

  private cleanupSession() {
    this.stopVideoPump();
    this.stopRingtone();
    this.clearRingDeadline();
    this.reportedRingCallId = null;
    this.stopLeftoverLocalAudio(this.session);
    this.session = null;
    if (this.remoteAudio) {
      try {
        this.remoteAudio.srcObject = null;
      } catch { /* ignore */ }
    }
    if (this.localVideoEl) {
      try { this.localVideoEl.srcObject = null; } catch { /* ignore */ }
    }
    if (this.remoteVideoEl) {
      try { this.remoteVideoEl.srcObject = null; } catch { /* ignore */ }
    }
    this.sdpAllowVideo = false;
    this.cameraFacingUser = true;
    const ended = this.activeCall;
    const claimingDoorbell = this.isDoorbellClaimInProgress();
    this.activeCall = null;
    this.emit();
    if (ended && ended.kind === 'phone') {
      try { eventBus.emit('softphone:call-ended'); } catch { /* ignore */ }
    } else if (ended && ended.kind === 'doorbell' && !claimingDoorbell) {
      // Preview cortada (otro atendió / CANCEL) o conversación ya activa que terminó.
      // Si hay claim/accept en curso, el Redirect cancela la pata vieja: no cerrar.
      try { eventBus.emit('softphone:doorbell-hangup'); } catch { /* ignore */ }
    }
    if (this.wantsRegistered && this.regStatus !== 'registered') {
      this.scheduleReconnectFallback(800);
    }
    return ended;
  }

  async start(credentials: SoftphoneCredentials): Promise<void> {
    if (this.startInFlight) {
      this.pushLog('info', 'start() ignorado: ya hay un arranque en curso');
      return;
    }
    this.startInFlight = true;
    try {
      await this.startUnlocked(credentials);
    } finally {
      this.startInFlight = false;
    }
  }

  private async startUnlocked(credentials: SoftphoneCredentials): Promise<void> {
    const nextExt = credentials.extension.trim();
    const liveCall = this.activeCall
      && (this.activeCall.phase === 'ringing'
        || this.activeCall.phase === 'active'
        || this.activeCall.phase === 'calling');
    if (liveCall) {
      this.pushLog('info', 'start() omitido: hay llamada en curso');
      return;
    }
    if (this.ua && this.regStatus === 'registered' && this.extension === nextExt) {
      this.credentials = credentials;
      this.pushLog('info', 'start() omitido: ya registrado');
      void import('../../utils/sessionFlags').then((flags) => {
        if (flags.isDoorbellIncomingContext()) {
          void this.notifyServerReady();
          return;
        }
        const incoming = flags.needsPhoneHandoff();
        void this.notifyServerReady({ reclaim: incoming && !this.activeCall });
      }).catch(() => {
        void this.notifyServerReady();
      });
      return;
    }
    if (this.ua || this.registerer) {
      await this.stop({ keepLogs: true, keepHandoff: true, keepCall: true });
    } else {
      this.releaseWarmedMic();
    }
    this.credentials = credentials;
    this.doorbellExt = credentials.doorbell_sip_extension;
    this.cameraId = credentials.camera_id ?? this.cameraId;
    this.extension = credentials.extension.trim();
    this.wantsRegistered = true;
    this.reregisterInFlight = false;
    this.regStatus = 'connecting';
    this.regError = null;
    this.markNotRegistered();
    this.emit();
    this.bindVisibilityLifecycle();
    this.startRegWatchdog();

    void import('./softphoneApi').then(({ fetchSoftphoneMe }) => {
      fetchSoftphoneMe().then((me) => {
        if (me?.config?.ring_timeout_sec) {
          this.ringTimeoutSec = Number(me.config.ring_timeout_sec) || 45;
        }
        if (me?.config?.camera_id) this.cameraId = me.config.camera_id;
      }).catch(() => { /* ignore */ });
    }).catch(() => { /* ignore */ });

    const ext = credentials.extension.trim();
    const domain = (credentials.ucm_host || window.location.hostname).split(':')[0];
    const wsServer = buildWsUrl(credentials.websocket_url);
    this.pushLog('info', `Iniciando registro SIP ext=${ext} domain=${domain}`);
    this.pushLog('info', `WebSocket ${wsServer}`);

    const uri = UserAgent.makeURI(`sip:${ext}@${domain}`);
    if (!uri) {
      this.regStatus = 'error';
      this.regError = 'URI SIP inválida';
      this.pushLog('error', 'URI SIP inválida');
      this.emit();
      return;
    }

    this.ua = new UserAgent({
      uri,
      authorizationUsername: ext,
      authorizationPassword: credentials.password,
      displayName: ext,
      // UCM/proxy suelen cortar WS idle (1006). Keep-alive CRLF evita el corte;
      // SIP.js reintenta el transport; nosotros renovamos REGISTER en onConnect.
      reconnectionAttempts: 30,
      reconnectionDelay: 4,
      transportOptions: {
        server: wsServer,
        keepAliveInterval: SipSoftphone.WS_KEEPALIVE_SEC,
        keepAliveDebounce: 10,
      },
      sessionDescriptionHandlerFactoryOptions: {
        iceGatheringTimeout: 5000,
        peerConnectionConfiguration: {
          iceServers: credentials.ice_servers?.length
            ? credentials.ice_servers
            : [{ urls: ['stun:stun.l.google.com:19302'] }],
        },
      },
      delegate: {
        onConnect: () => {
          this.pushLog('ok', 'WebSocket conectado a UCM');
          this.clearReconnectFallback();
          // Solo re-REGISTER en reconnect (tras el primer start ya se registra abajo).
          if (this.registerer && this.regStatus !== 'registered') {
            void this.ensureRegisteredAfterConnect();
          }
        },
        onDisconnect: (error?: Error) => {
          const detail = error?.message || 'sin detalle';
          this.pushLog('warn', `WebSocket desconectado: ${detail}`);
          if (!this.wantsRegistered) return;
          if (this.regStatus === 'connecting' || this.regStatus === 'registered') {
            this.regStatus = 'unregistered';
            this.regError = detail;
            this.markNotRegistered();
            this.emit();
          }
          // SIP.js reintenta el WS; si no vuelve, fallback a start() completo.
          this.scheduleReconnectFallback(12000);
        },
        onInvite: (invitation: Invitation) => {
          const peer = peerFromUri(invitation.remoteIdentity?.uri?.toString())
            || peerFromUri(invitation.request.from?.uri?.toString())
            || 'unknown';
          const kind = this.classifyKind(peer);
          if (this.blocksKind(kind)) {
            this.pushLog('info', `INVITE ignorado (No molestar ${kind}) — 486 Busy`);
            void invitation.reject({ statusCode: 486, reasonPhrase: 'Busy Here' }).catch(() => { /* ignore */ });
            return;
          }
          this.setupSession(invitation, 'in', peer, kind);
          if (this.pendingAccept) {
            this.pendingAccept = false;
            void this.accept().catch(() => { /* ignore */ });
          }
        },
      },
    });

    this.ua.stateChange.addListener((state: UserAgentState) => {
      this.pushLog('info', `UserAgent estado=${state}`);
      if (state === UserAgentState.Stopped) {
        this.regStatus = 'unregistered';
        this.emit();
      }
    });

    try {
      this.pushLog('info', 'Arrancando UserAgent…');
      await this.ua.start();
      this.registerer = new Registerer(this.ua);
      this.registerer.stateChange.addListener((state: RegistererState) => {
        if (state === RegistererState.Registered) {
          this.regStatus = 'registered';
          this.regError = null;
          this.markRegisteredOk();
          this.clearReconnectFallback();
          this.pushLog('ok', `REGISTER OK — interno ${ext}`);
          // Handoff: ready; reclaim si hay intent entrante y aún no llegó el INVITE.
          void import('../../utils/sessionFlags').then((flags) => {
            if (flags.isDoorbellIncomingContext()) {
              void this.notifyServerReady();
              return;
            }
            const incoming = flags.needsPhoneHandoff();
            void this.notifyServerReady({ reclaim: incoming && !this.activeCall });
          }).catch(() => {
            void this.notifyServerReady();
          });
        } else if (state === RegistererState.Unregistered) {
          this.regStatus = 'unregistered';
          this.markNotRegistered();
          this.pushLog('warn', 'REGISTER: no registrado');
        } else if (state === RegistererState.Initial) {
          this.regStatus = 'connecting';
          this.markNotRegistered();
          this.pushLog('info', 'REGISTER: inicial');
        } else {
          this.pushLog('info', `REGISTER estado=${state}`);
        }
        this.emit();
      });
      this.pushLog('info', 'Enviando REGISTER…');
      await this.registerer.register();
    } catch (e: unknown) {
      const msg = errMsg(e);
      this.regStatus = 'error';
      this.regError = msg;
      this.pushLog('error', `Fallo registro: ${msg}`);
      this.emit();
    }
  }

  async stop(opts?: { keepLogs?: boolean; keepHandoff?: boolean; keepCall?: boolean }): Promise<void> {
    const keepCall = Boolean(opts?.keepCall) && Boolean(
      this.activeCall
      && (this.activeCall.phase === 'ringing'
        || this.activeCall.phase === 'active'
        || this.activeCall.phase === 'calling'),
    );
    if (keepCall) {
      this.pushLog('info', 'stop() omitido: hay llamada en curso');
      return;
    }
    this.wantsRegistered = false;
    this.clearReconnectFallback();
    this.stopRegWatchdog();
    if (!opts?.keepHandoff) this.stopHandoffRetries();
    this.stopRingtone();
    this.releaseWarmedMic();
    this.clearRingDeadline();
    this.unbindVisibilityLifecycle();
    try {
      if (this.session) {
        await this.hangup();
      }
    } catch { /* ignore */ }
    try {
      await this.registerer?.unregister();
    } catch { /* ignore */ }
    try {
      await this.ua?.stop();
    } catch { /* ignore */ }
    this.registerer = null;
    this.ua = null;
    this.credentials = null;
    this.regStatus = 'idle';
    this.regError = null;
    this.activeCall = null;
    this.reportedRingCallId = null;
    if (!opts?.keepLogs) {
      /* leave logs for troubleshooting across restart */
    }
    this.emit();
  }

  async reregister(): Promise<void> {
    if (this.reregisterInFlight || this.startInFlight) return;
    if (this.activeCall && (this.activeCall.phase === 'ringing' || this.activeCall.phase === 'active' || this.activeCall.phase === 'calling')) {
      this.pushLog('info', 'Re-REGISTER omitido: hay llamada en curso');
      if (this.registerer && this.regStatus !== 'registered') {
        try { await this.registerer.register(); } catch { /* ignore */ }
      }
      return;
    }
    this.reregisterInFlight = true;
    this.pushLog('info', 'Reintento de registro solicitado');
    try {
      const { fetchSoftphoneCredentials } = await import('./softphoneApi');
      const creds = await fetchSoftphoneCredentials();
      await this.start(creds);
    } catch (e: unknown) {
      this.noteBootstrapError(e);
      if (this.wantsRegistered) this.scheduleReconnectFallback(10000);
    } finally {
      this.reregisterInFlight = false;
    }
  }

  private sdpModifiers() {
    return [
      async (description: RTCSessionDescriptionInit) => {
        if (!description.sdp) return description;
        const { sdp: cleaned, notes } = sanitizeSdpForWebRtc(
          description.sdp,
          description.type,
          this.sdpAllowVideo,
        );
        if (notes.length) {
          this.pushLog('info', `SDP[${description.type || '?'}]: ${notes.join('; ')}`);
        }
        return { ...description, sdp: cleaned };
      },
    ];
  }

  private mediaOptions(video: boolean) {
    const constraints: MediaStreamConstraints = video
      ? { audio: true, video: { facingMode: this.cameraFacingUser ? 'user' : 'environment' } }
      : { audio: true, video: false };
    return {
      constraints,
      sessionDescriptionHandlerModifiers: this.sdpModifiers(),
    };
  }

  async call(target: string): Promise<void> {
    if (!this.ua || !this.credentials) throw new Error('SoftPhone no registrado');
    const digits = String(target || '').trim();
    if (!digits) throw new Error('Número vacío');
    const domain = (this.credentials.ucm_host || window.location.hostname).split(':')[0];
    const targetUri = UserAgent.makeURI(`sip:${digits}@${domain}`);
    if (!targetUri) throw new Error('Destino inválido');
    const kind = this.classifyKind(digits);
    const wantVideo = this.wantsDeviceVideo(kind, digits);
    await this.warmupMic();
    // Audio por defecto. En interno se deja pasar m=video remoto (recv) por si luego activan la UI.
    this.sdpAllowVideo = wantVideo;
    this.cameraFacingUser = true;
    const media = this.mediaOptions(false);
    const iceMs = SipSoftphone.OUTBOUND_ICE_GATHER_MS;
    const outboundSdh = {
      constraints: media.constraints,
      iceGatheringTimeout: iceMs,
    };
    const inviter = new Inviter(this.ua, targetUri, {
      sessionDescriptionHandlerModifiers: media.sessionDescriptionHandlerModifiers,
      sessionDescriptionHandlerOptions: outboundSdh,
    });
    this.setupSession(inviter, 'out', digits, kind);
    if (this.activeCall) {
      this.activeCall = { ...this.activeCall, wantsVideo: wantVideo, cameraOn: false, videoUiOn: false };
    }
    const t0 = Date.now();
    this.pushLog('info', `INVITE saliente a ${digits} audio (ICE máx ${iceMs} ms)`);
    await inviter.invite({
      sessionDescriptionHandlerModifiers: media.sessionDescriptionHandlerModifiers,
      sessionDescriptionHandlerOptions: outboundSdh,
    });
    this.attachLocalPreview();
    this.pushLog('ok', `INVITE enviado a ${digits} en ${Date.now() - t0} ms`);
  }

  private clearWarmedMicIdleTimer(): void {
    if (!this.warmedMicIdleTimer) return;
    clearTimeout(this.warmedMicIdleTimer);
    this.warmedMicIdleTimer = null;
  }

  private armWarmedMicIdleRelease(): void {
    this.clearWarmedMicIdleTimer();
    this.warmedMicIdleTimer = setTimeout(() => {
      this.warmedMicIdleTimer = null;
      const live = Boolean(
        this.activeCall
        && (this.activeCall.phase === 'ringing'
          || this.activeCall.phase === 'active'
          || this.activeCall.phase === 'calling'),
      );
      if (live || this.pendingAccept || this.acceptInFlight) return;
      this.releaseWarmedMic();
      this.pushLog('info', 'Mic liberado (idle)');
    }, SipSoftphone.WARMED_MIC_IDLE_MS);
  }

  private stopLeftoverLocalAudio(session: Session | null): void {
    if (session) {
      try {
        const pc = getPeerConnection(session);
        pc?.getSenders().forEach((s) => {
          const track = s.track;
          if (!track || track.kind !== 'audio') return;
          try { track.stop(); } catch { /* ignore */ }
          try { void s.replaceTrack?.(null); } catch { /* ignore */ }
        });
      } catch { /* ignore */ }
    }
    // Un CANCEL del Redirect durante Atender no debe soltar el mic del gesto.
    if (!this.isDoorbellClaimInProgress()) this.releaseWarmedMic();
  }

  private releaseWarmedMic(): void {
    this.clearWarmedMicIdleTimer();
    if (!this.warmedMic) return;
    try {
      this.warmedMic.getTracks().forEach((t) => t.stop());
    } catch { /* ignore */ }
    this.warmedMic = null;
  }

  /**
   * Pedir mic en el gesto de Atender (iOS/Android) para que accept() no cuelgue.
   * `idle`: solo móvil, se suelta a los 10s si no hay llamada.
   */
  async warmupMic(mode: 'call' | 'idle' = 'call'): Promise<void> {
    if (mode === 'idle' && !isMobileUa()) return;
    if (mode === 'call') this.clearWarmedMicIdleTimer();
    if (!navigator.mediaDevices?.getUserMedia) return;
    const live = this.warmedMic?.getAudioTracks().some((t) => t.readyState === 'live');
    if (!live) {
      try {
        this.releaseWarmedMic();
        this.warmedMic = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
        this.pushLog('ok', 'Mic listo');
      } catch (e: unknown) {
        this.pushLog('warn', `Mic warmup: ${errMsg(e)}`);
        return;
      }
    }
    if (mode === 'idle') this.armWarmedMicIdleRelease();
  }

  /** El usuario tocó Atender antes de que llegue el INVITE. */
  queueAccept(): void {
    this.pendingAccept = true;
    void this.warmupMic('call');
    this.emit();
  }

  clearPendingAccept(): void {
    this.pendingAccept = false;
    const live = Boolean(
      this.session
      || this.acceptInFlight
      || (this.activeCall
        && (this.activeCall.phase === 'ringing'
          || this.activeCall.phase === 'active'
          || this.activeCall.phase === 'calling')),
    );
    if (!live) this.releaseWarmedMic();
  }

  async accept(): Promise<void> {
    const session = this.session;
    if (!session || !(session instanceof Invitation)) return;
    this.acceptInFlight = true;
    this.stopRingtone();
    this.emit();
    const kind = this.activeCall?.kind || 'phone';
    const peer = this.activeCall?.peer || '';
    const wantVideo = this.wantsDeviceVideo(kind, peer);
    await this.warmupMic();
    if (kind === 'doorbell') {
      this.sdpAllowVideo = false;
    } else {
      // Interno: no pedir cámara al atender; sí aceptar m=video del aparato (recvonly).
      this.sdpAllowVideo = wantVideo;
    }
    const media = this.mediaOptions(false);
    try {
      await session.accept({
        sessionDescriptionHandlerModifiers: media.sessionDescriptionHandlerModifiers,
        sessionDescriptionHandlerOptions: { constraints: media.constraints },
      });
      if (this.activeCall) {
        this.activeCall = {
          ...this.activeCall,
          wantsVideo: wantVideo,
          cameraOn: false,
          videoUiOn: false,
        };
      }
    } catch (e: unknown) {
      this.pushLog('error', `Accept falló: ${errMsg(e)}`);
      this.acceptInFlight = false;
      this.emit();
      throw e;
    }
    this.acceptInFlight = false;
    if (this.activeCall) {
      this.activeCall = { ...this.activeCall, phase: 'active' };
      this.emit();
    } else {
      this.emit();
    }
    if (this.activeCall?.kind === 'phone') {
      this.attachLocalPreview();
    }
    void this.applySpeaker(this.activeCall?.speaker ?? false);
  }

  async hangup(): Promise<SoftphoneActiveCall | null> {
    this.pendingAccept = false;
    this.stopRingtone();
    const session = this.session;
    const snapshot = this.activeCall;
    if (!session) {
      this.cleanupSession();
      this.releaseWarmedMic();
      return snapshot;
    }
    try {
      switch (session.state) {
        case SessionState.Initial:
        case SessionState.Establishing:
          if (session instanceof Inviter) await session.cancel();
          else if (session instanceof Invitation) await session.reject();
          break;
        case SessionState.Established:
          await session.bye();
          break;
        default:
          break;
      }
    } catch { /* ignore */ }
    const ended = this.cleanupSession() || snapshot;
    this.releaseWarmedMic();
    return ended;
  }

  async setMute(muted: boolean): Promise<void> {
    if (!this.session || !this.activeCall) return;
    try {
      const pc = getPeerConnection(this.session);
      pc?.getSenders().forEach((s) => {
        if (s.track && s.track.kind === 'audio') s.track.enabled = !muted;
      });
      this.activeCall = { ...this.activeCall, muted };
      this.emit();
    } catch { /* ignore */ }
  }

  async setSpeaker(speaker: boolean): Promise<void> {
    if (!this.activeCall) return;
    this.activeCall = { ...this.activeCall, speaker };
    this.emit();
    await this.routeAudioOutput(speaker);
  }

  async sendDtmf(tone: string): Promise<void> {
    if (!this.session || this.session.state !== SessionState.Established) return;
    try {
      const sdh = this.session.sessionDescriptionHandler as { sendDtmf?: (t: string) => void } | undefined;
      if (sdh && typeof sdh.sendDtmf === 'function') {
        sdh.sendDtmf(tone);
        return;
      }
      await this.session.info({
        requestOptions: {
          body: {
            contentDisposition: 'render',
            contentType: 'application/dtmf-relay',
            content: `Signal=${tone}\r\nDuration=160`,
          },
        },
      });
    } catch { /* ignore */ }
  }
}

/** Singleton de sesión SIP para App + SoftphonePage + overlay timbre. */
export const sipSoftphone = new SipSoftphone();
